import { useMemo, useCallback } from "react";

/**
 * Hook que encapsula todos los cálculos de BTL (Bloque D).
 *
 * Parametriza 3 patrones repetidos (ingresos, comisión edificios, otros costos)
 * para cubrir las 5 variantes (genérico, año anterior, Chile, Perú, otro país).
 */
export function useBTLCalcs({
  salesBTL,
  salesBTLAnoAnterior,
  salesBTLChile,
  salesBTLPeru,
  salesBTLOtroPais,
  selectedYear,
  monthsToShow,
  modoCombinado,
}) {
  // ---------------------------------------------------------------------------
  // Helpers parametrizados
  // ---------------------------------------------------------------------------
  const calcIngresosBTL = useCallback((salesData, filterYear = null) => {
    const mapa = Array(12).fill(0);
    salesData.forEach((row) => {
      if (!row.facturafecha) return;
      const fecha = new Date(row.facturafecha);
      if (filterYear !== null && fecha.getFullYear() !== filterYear) return;
      const mes = fecha.getMonth();
      mapa[mes] += Number(row.total_investment) || 0;
    });
    return mapa;
  }, []);

  const calcComisionEdificiosBTL = useCallback((salesData, filterYear = null) => {
    const mapa = Array(12).fill(0);
    salesData.forEach((row) => {
      if (!row.facturafecha || !row.buildings) return;
      const fecha = new Date(row.facturafecha);
      if (filterYear !== null && fecha.getFullYear() !== filterYear) return;
      const mes = fecha.getMonth();
      const buildings = typeof row.buildings === "string" ? JSON.parse(row.buildings) : row.buildings;
      buildings.forEach((building) => {
        const importe = Number(building.importe) || 0;
        const porcentaje = Number(building.comision) || 0;
        mapa[mes] += importe * (porcentaje / 100);
      });
    });
    return mapa;
  }, []);

  const calcOtrosCostosBTL = useCallback((salesData, filterYear = null) => {
    const mapa = Array(12).fill(0);
    salesData.forEach((row) => {
      if (!row.facturafecha) return;
      const fecha = new Date(row.facturafecha);
      if (filterYear !== null && fecha.getFullYear() !== filterYear) return;
      const mes = fecha.getMonth();
      mapa[mes] += Number(row.otros_costos) || 0;
    });
    return mapa;
  }, []);

  // ---------------------------------------------------------------------------
  // Ingresos BTL (5 variantes)
  // ---------------------------------------------------------------------------
  const ingresosBTLMensual = useMemo(() => calcIngresosBTL(salesBTL), [calcIngresosBTL, salesBTL]);

  const ingresosBTLMensualAnoAnterior = useMemo(
    () => calcIngresosBTL(salesBTLAnoAnterior),
    [calcIngresosBTL, salesBTLAnoAnterior]
  );

  const ingresosBTLMensualChile = useMemo(
    () => calcIngresosBTL(salesBTLChile, selectedYear),
    [calcIngresosBTL, salesBTLChile, selectedYear]
  );

  const ingresosBTLMensualPeru = useMemo(
    () => calcIngresosBTL(salesBTLPeru, selectedYear),
    [calcIngresosBTL, salesBTLPeru, selectedYear]
  );

  // eslint-disable-next-line no-unused-vars
  const ingresosBTLOtroPaisMensual = useMemo(() => {
    if (!modoCombinado) return Array(12).fill(0);
    return calcIngresosBTL(salesBTLOtroPais, selectedYear);
  }, [modoCombinado, calcIngresosBTL, salesBTLOtroPais, selectedYear]);

  // ---------------------------------------------------------------------------
  // Comisión edificios BTL (4 variantes)
  // ---------------------------------------------------------------------------
  const comisionEdificiosBTLMensual = useMemo(
    () => calcComisionEdificiosBTL(salesBTL),
    [calcComisionEdificiosBTL, salesBTL]
  );

  const comisionEdificiosBTLMensualAnoAnterior = useMemo(
    () => calcComisionEdificiosBTL(salesBTLAnoAnterior),
    [calcComisionEdificiosBTL, salesBTLAnoAnterior]
  );

  const comisionEdificiosBTLMensualChile = useMemo(
    () => calcComisionEdificiosBTL(salesBTLChile, selectedYear),
    [calcComisionEdificiosBTL, salesBTLChile, selectedYear]
  );

  const comisionEdificiosBTLMensualPeru = useMemo(
    () => calcComisionEdificiosBTL(salesBTLPeru, selectedYear),
    [calcComisionEdificiosBTL, salesBTLPeru, selectedYear]
  );

  // ---------------------------------------------------------------------------
  // Otros costos BTL (4 variantes)
  // ---------------------------------------------------------------------------
  const otrosCostosBTLMensual = useMemo(() => calcOtrosCostosBTL(salesBTL), [calcOtrosCostosBTL, salesBTL]);

  const otrosCostosBTLMensualAnoAnterior = useMemo(
    () => calcOtrosCostosBTL(salesBTLAnoAnterior),
    [calcOtrosCostosBTL, salesBTLAnoAnterior]
  );

  const otrosCostosBTLMensualChile = useMemo(
    () => calcOtrosCostosBTL(salesBTLChile, selectedYear),
    [calcOtrosCostosBTL, salesBTLChile, selectedYear]
  );

  const otrosCostosBTLMensualPeru = useMemo(
    () => calcOtrosCostosBTL(salesBTLPeru, selectedYear),
    [calcOtrosCostosBTL, salesBTLPeru, selectedYear]
  );

  // ---------------------------------------------------------------------------
  // Margen BTL mensual
  // ---------------------------------------------------------------------------
  const margenBTLMensual = useMemo(
    () => ingresosBTLMensual.map((ing, i) => ing - comisionEdificiosBTLMensual[i] - otrosCostosBTLMensual[i]),
    [ingresosBTLMensual, comisionEdificiosBTLMensual, otrosCostosBTLMensual]
  );

  // ---------------------------------------------------------------------------
  // YTDs
  // ---------------------------------------------------------------------------
  const ytdIngresosBTL = useMemo(
    () => ingresosBTLMensual.slice(0, monthsToShow).reduce((a, b) => a + b, 0),
    [ingresosBTLMensual, monthsToShow]
  );

  const ytdComisionEdificiosBTL = useMemo(
    () => comisionEdificiosBTLMensual.slice(0, monthsToShow).reduce((a, b) => a + b, 0),
    [comisionEdificiosBTLMensual, monthsToShow]
  );

  const ytdOtrosCostosBTL = useMemo(
    () => otrosCostosBTLMensual.slice(0, monthsToShow).reduce((a, b) => a + b, 0),
    [otrosCostosBTLMensual, monthsToShow]
  );

  const ytdMargenBTL = useMemo(
    () => margenBTLMensual.slice(0, monthsToShow).reduce((a, b) => a + b, 0),
    [margenBTLMensual, monthsToShow]
  );

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------
  return {
    // Ingresos
    ingresosBTLMensual,
    ingresosBTLMensualAnoAnterior,
    ingresosBTLMensualChile,
    ingresosBTLMensualPeru,
    ingresosBTLOtroPaisMensual,
    // Comisión edificios
    comisionEdificiosBTLMensual,
    comisionEdificiosBTLMensualAnoAnterior,
    comisionEdificiosBTLMensualChile,
    comisionEdificiosBTLMensualPeru,
    // Otros costos
    otrosCostosBTLMensual,
    otrosCostosBTLMensualAnoAnterior,
    otrosCostosBTLMensualChile,
    otrosCostosBTLMensualPeru,
    // Margen
    margenBTLMensual,
    // YTDs
    ytdIngresosBTL,
    ytdComisionEdificiosBTL,
    ytdOtrosCostosBTL,
    ytdMargenBTL,
  };
}
